import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { api, unwrap } from "./client";
import { connectAttendanceStream, useAttendanceLive } from "./stream";
import type { AgentRolloutDevice, Device, DeviceConnection, LiveAttendance, NetworkDevice, ScreenshotRemovalRequest, UpdateAgentRolloutRequest, User } from "./types";

/**
 * Query keys in one place. A mutation invalidates by prefix, for example
 * ["attendance"] refreshes my state, the live board and the counts together.
 */
export const keys = {
  me: ["me"] as const,
  users: ["users"] as const,
  devices: ["devices"] as const,
  allDevices: ["devices", "all"] as const,
  allUsers: ["users", "all"] as const,
  jobRoles: ["job-roles"] as const,
  teams: ["teams"] as const,
  team: (id: string) => ["teams", id] as const,
  tenants: ["tenants"] as const,
  organisation: ["organisation"] as const,
  organisationPlan: ["organisation", "plan"] as const,
  attendance: ["attendance"] as const,
  myAttendance: ["attendance", "me"] as const,
  liveAttendance: ["attendance", "live"] as const,
  allLiveAttendance: ["attendance", "live", "all"] as const,
  attendanceCounts: ["attendance", "counts"] as const,
  attendanceSettings: ["attendance", "settings"] as const,
  statuses: ["attendance", "statuses"] as const,
  activity: (from: string, to: string, userId?: string) => ["activity", from, to, userId ?? "all"] as const,
  network: ["network"] as const,
  networkSettings: ["network", "settings"] as const,
  networkDevices: ["network", "devices"] as const,
  networkAllDevices: ["network", "devices", "all"] as const,
  networkRatings: (deviceIds: readonly string[]) => ["network", "ratings", ...deviceIds] as const,
  policy: ["policy"] as const,
  agentConfig: ["policy", "agent-config"] as const,
  agentRelease: ["agent", "release"] as const,
  agentRollout: ["agent", "rollout"] as const,
  agentRolloutDevices: ["agent", "rollout", "devices"] as const,
  picture: (userId: string, version: string) => ["picture", userId, version] as const,
  logo: (version: string) => ["logo", version] as const,
  deviceConnection: (code: string) => ["auth", "device-connection", code] as const,
  screenshots: ["screenshots"] as const,
  /** One person's screenshots of one day; "me" for the signed-in person's own. */
  screenshotsDay: (userId: string, day: string) => ["screenshots", "day", userId, day] as const,
  removalRequests: (state: RemovalRequestState) => ["screenshots", "removal-requests", state] as const,
  // The images are not under the "screenshots" prefix: a removal request or a decision refreshes
  // the lists, and the pictures themselves never change under the same id.
  screenshotImage: (id: string) => ["screenshot-image", id] as const,
  screenshotThumb: (id: string) => ["screenshot-thumb", id] as const,
};

/** The two views of the removal queue: waiting for an admin, or already decided. */
export type RemovalRequestState = "pending" | "decided";

const PAGE_SIZE = 50;
type Page<T> = { items: T[]; next_cursor?: string };
const nextCursor = <T,>(page: Page<T>) => page.next_cursor;

/** The signed-in person with their organisation (for its name and time zone). */
export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => unwrap(api.GET("/v1/me")),
    staleTime: 5 * 60_000,
  });
}

export function useUsers() {
  return useInfiniteQuery({
    queryKey: keys.users,
    queryFn: ({ pageParam }) => unwrap(api.GET("/v1/users", { params: { query: { cursor: pageParam, limit: PAGE_SIZE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
  });
}

/** The organisation's job roles (job titles), sorted by name. */
export function useJobRoles(enabled = true) {
  return useQuery({
    queryKey: keys.jobRoles,
    queryFn: () => unwrap(api.GET("/v1/job-roles")),
    enabled,
    select: (data) => data.items,
  });
}

export function useTeams() {
  return useInfiniteQuery({
    queryKey: keys.teams,
    queryFn: ({ pageParam }) => unwrap(api.GET("/v1/teams", { params: { query: { cursor: pageParam, limit: 200 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
  });
}

export function useTeam(id: string) {
  return useQuery({
    queryKey: keys.team(id),
    queryFn: () => unwrap(api.GET("/v1/teams/{team_id}", { params: { path: { team_id: id } } })),
  });
}

/** Up to 200 people in one page, for pickers. Larger organisations will need search (see the roadmap). */
export function usePeopleForPicker(enabled: boolean) {
  return useQuery({
    queryKey: [...keys.users, "picker"] as const,
    queryFn: () => unwrap(api.GET("/v1/users", { params: { query: { limit: 200 } } })),
    enabled,
  });
}

export function useDevices() {
  return useInfiniteQuery({
    queryKey: keys.devices,
    queryFn: ({ pageParam }) => unwrap(api.GET("/v1/devices", { params: { query: { cursor: pageParam, limit: PAGE_SIZE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
  });
}

/** The API's largest page of a list. */
const LARGEST_PAGE = 200;

/** Every page of a list, in as few requests as the API allows (200 items each). */
async function everyPage<T>(fetchPage: (cursor: string | undefined) => Promise<Page<T>>): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | undefined;
  do {
    const page = await fetchPage(cursor);
    items.push(...page.items);
    cursor = page.next_cursor;
  } while (cursor);
  return items;
}

/** Every device in the organisation, for views that join them to people: one request per 200 devices. */
export function useAllDevices() {
  return useQuery<Device[]>({
    queryKey: keys.allDevices,
    queryFn: () => everyPage((cursor) => unwrap(api.GET("/v1/devices", { params: { query: { cursor, limit: LARGEST_PAGE } } }))),
    staleTime: 60_000,
  });
}

/** Everyone in the organisation, for views that show people next to their devices: one request per 200 people. */
export function useAllPeople() {
  return useQuery<User[]>({
    queryKey: keys.allUsers,
    queryFn: () => everyPage((cursor) => unwrap(api.GET("/v1/users", { params: { query: { cursor, limit: LARGEST_PAGE } } }))),
    staleTime: 60_000,
  });
}

export function useTenants() {
  return useInfiniteQuery({
    queryKey: keys.tenants,
    queryFn: ({ pageParam }) => unwrap(api.GET("/v1/tenants", { params: { query: { cursor: pageParam, limit: PAGE_SIZE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
  });
}

/** The signed-in person's own organisation: name, short name and time zone. */
export function useOrganisation() {
  return useQuery({
    queryKey: keys.organisation,
    queryFn: () => unwrap(api.GET("/v1/organisation")),
  });
}

/** The organisation's plan and how many devices it uses against the plan's limit. Admins and managers. */
export function useOrganisationPlan() {
  return useQuery({
    queryKey: keys.organisationPlan,
    queryFn: () => unwrap(api.GET("/v1/organisation/plan")),
  });
}

/**
 * How often every view of attendance asks the server again: Live, the counts on Overview, the
 * status columns on People and in a team, and My day. While the attendance stream is connected
 * (`useAttendanceStream`) changes arrive as they happen and this is only a fallback, every
 * minute; without the stream it is every 15 seconds, so a change made in the Windows agent's
 * tray still shows within that time wherever a person looks, in a tab left open in the
 * background too (the browser slows a background tab's timers, but the next answer still
 * comes), and at once when the tab regains focus (the client's `refetchOnWindowFocus`).
 */
export const ATTENDANCE_REFRESH_MS = 15_000;
export const ATTENDANCE_STREAMING_REFRESH_MS = 60_000;

/** How long after a streamed change the attendance queries are fetched again, so a burst (an edit moving 50 people) is one fetch. */
export const ATTENDANCE_STREAM_SETTLE_MS = 300;

function useAttendancePolling() {
  const live = useAttendanceLive();
  return { refetchInterval: live ? ATTENDANCE_STREAMING_REFRESH_MS : ATTENDANCE_REFRESH_MS, refetchIntervalInBackground: true } as const;
}

/**
 * Keep the attendance stream open while the signed-in person belongs to an organisation (the
 * shell mounts it once). Every change the server pushes, and every (re)connection, fetches my
 * state, the live list (both pages and the whole list) and the counts again, so Live, My day,
 * People, Teams and the Overview show the change within a second.
 */
export function useAttendanceStream(enabled: boolean) {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    const refresh = () => {
      for (const queryKey of [keys.myAttendance, keys.liveAttendance, keys.attendanceCounts]) {
        void queryClient.invalidateQueries({ queryKey });
      }
    };
    let settle: ReturnType<typeof setTimeout> | undefined;
    const stop = connectAttendanceStream({
      onConnect: refresh,
      onChange: () => {
        settle ??= setTimeout(() => {
          settle = undefined;
          refresh();
        }, ATTENDANCE_STREAM_SETTLE_MS);
      },
    });
    return () => {
      clearTimeout(settle);
      stop();
    };
  }, [enabled, queryClient]);
}

export function useLiveAttendance() {
  const polling = useAttendancePolling();
  return useInfiniteQuery({
    queryKey: keys.liveAttendance,
    queryFn: ({ pageParam }) => unwrap(api.GET("/v1/attendance/live", { params: { query: { cursor: pageParam, limit: PAGE_SIZE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
    ...polling,
  });
}

/**
 * Everyone's current status, for views that show it beside a list they already have (People,
 * a team's members): one request per 200 people, never one per person, as changes arrive.
 */
export function useAllLiveAttendance(enabled = true) {
  const polling = useAttendancePolling();
  return useQuery<LiveAttendance[]>({
    queryKey: keys.allLiveAttendance,
    queryFn: () => everyPage((cursor) => unwrap(api.GET("/v1/attendance/live", { params: { query: { cursor, limit: LARGEST_PAGE } } }))),
    enabled,
    ...polling,
  });
}

export function useAttendanceCounts() {
  const polling = useAttendancePolling();
  return useQuery({
    queryKey: keys.attendanceCounts,
    queryFn: () => unwrap(api.GET("/v1/attendance/counts")),
    ...polling,
  });
}

/** The caller's status, with the organisation's `checkin_sources` (whether My day may change it). */
export function useMyAttendance() {
  const polling = useAttendancePolling();
  return useQuery({
    queryKey: keys.myAttendance,
    queryFn: () => unwrap(api.GET("/v1/attendance/me")),
    ...polling,
  });
}

/** The organisation's own statuses (Working, Break, Meeting…), in the order to show them. */
export function useStatuses(enabled = true) {
  return useQuery({
    queryKey: keys.statuses,
    queryFn: () => unwrap(api.GET("/v1/attendance/statuses")),
    enabled,
  });
}

/** Where the organisation's people may check in: only on the Windows agent, or also on the web and phones. */
export function useAttendanceSettings() {
  return useQuery({
    queryKey: keys.attendanceSettings,
    queryFn: () => unwrap(api.GET("/v1/attendance/settings")),
  });
}

/** Activity totals for an inclusive range of days ("YYYY-MM-DD"). */
export function useActivitySummary(range: { from: string; to: string } | undefined, userId?: string) {
  return useQuery({
    queryKey: keys.activity(range?.from ?? "", range?.to ?? "", userId),
    queryFn: () =>
      unwrap(api.GET("/v1/activity/summary", { params: { query: { from: range!.from, to: range!.to, user_id: userId } } })),
    enabled: range !== undefined,
    staleTime: 60_000,
  });
}

/** Whether the organisation's computers measure their network, and how often. */
export function useNetworkSettings() {
  return useQuery({
    queryKey: keys.networkSettings,
    queryFn: () => unwrap(api.GET("/v1/network/settings")),
  });
}

/** The organisation's computers with the person on each and their network rating. */
export function useNetworkDevices() {
  return useInfiniteQuery({
    queryKey: keys.networkDevices,
    queryFn: ({ pageParam }) => unwrap(api.GET("/v1/network/devices", { params: { query: { cursor: pageParam, limit: PAGE_SIZE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
    staleTime: 60_000,
  });
}

/** The API's largest page of network devices. */
const DEVICES_PER_REQUEST = 200;

/**
 * Every computer in the organisation with its latest measurement, in as few requests as
 * the API allows (200 per page), for views that join them to people. One request per
 * 200 computers, never one per person.
 */
export function useAllNetworkDevices(enabled = true) {
  return useQuery({
    queryKey: keys.networkAllDevices,
    queryFn: async () => {
      const devices: NetworkDevice[] = [];
      let cursor: string | undefined;
      do {
        const page = await unwrap(api.GET("/v1/network/devices", { params: { query: { cursor, limit: DEVICES_PER_REQUEST } } }));
        devices.push(...page.items);
        cursor = page.next_cursor;
      } while (cursor);
      return devices;
    },
    enabled,
    staleTime: 60_000,
  });
}

/** The API answers for at most this many devices per request. */
const RATINGS_PER_REQUEST = 200;

/**
 * Network ratings for a list of devices, as a map by device id. Long lists are asked
 * for in parts. The previous answer stays on screen while a longer list loads.
 */
export function useNetworkRatings(deviceIds: readonly string[]) {
  return useQuery({
    queryKey: keys.networkRatings(deviceIds),
    queryFn: async () => {
      const parts: string[][] = [];
      for (let i = 0; i < deviceIds.length; i += RATINGS_PER_REQUEST) parts.push(deviceIds.slice(i, i + RATINGS_PER_REQUEST));
      const answers = await Promise.all(
        parts.map((part) => unwrap(api.GET("/v1/network/ratings", { params: { query: { device_id: part } } }))),
      );
      return new Map(answers.flatMap((answer) => answer.items).map((rating) => [rating.device_id, rating]));
    },
    enabled: deviceIds.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/** What the Zotula agent records in the organisation: apps, window titles, idle time, screenshots. */
export function usePolicy() {
  return useQuery({
    queryKey: keys.policy,
    queryFn: () => unwrap(api.GET("/v1/policy")),
  });
}

/**
 * What the agent on a computer is told to do: the policy and the network monitor's setting.
 * Everyone in the organisation may read it, so My day shows exactly what the agent obeys.
 */
export function useAgentConfig() {
  return useQuery({
    queryKey: keys.agentConfig,
    queryFn: () => unwrap(api.GET("/v1/agent/config")),
    staleTime: 5 * 60_000,
  });
}

/**
 * The Windows agent's latest release: the version this server was built from, the installer
 * when one is published, the address agents connect to and what changed. Admins and managers.
 */
export function useAgentRelease() {
  return useQuery({
    queryKey: keys.agentRelease,
    queryFn: () => unwrap(api.GET("/v1/agent/release")),
    staleTime: 5 * 60_000,
  });
}

/**
 * How the organisation's Windows computers update the agent (automatic, pilot first or
 * manual), and whether updates can run on this server at all. Admins and managers.
 */
export function useAgentRollout() {
  return useQuery({
    queryKey: keys.agentRollout,
    queryFn: () => unwrap(api.GET("/v1/agent/rollout")),
    staleTime: 60_000,
  });
}

/**
 * Every computer's part in the rollout (pilot, an "update now" request, the last update
 * state its agent reported): one request per 200 computers, never one per computer.
 */
export function useAllAgentRolloutDevices() {
  return useQuery<AgentRolloutDevice[]>({
    queryKey: keys.agentRolloutDevices,
    queryFn: () => everyPage((cursor) => unwrap(api.GET("/v1/agent/rollout/devices", { params: { query: { cursor, limit: LARGEST_PAGE } } }))),
    staleTime: 60_000,
  });
}

/** Change the rollout setting (admins). The answer replaces the cached setting. */
export function useUpdateAgentRollout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateAgentRolloutRequest) => unwrap(api.PUT("/v1/agent/rollout", { body })),
    onSuccess: (saved) => queryClient.setQueryData(keys.agentRollout, saved),
  });
}

/** Release the published version to every computer: "Roll out to everyone" and "Update all" (admins). */
export function useReleaseAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/v1/agent/rollout/release")),
    onSuccess: (saved) => queryClient.setQueryData(keys.agentRollout, saved),
  });
}

/** Put one computer's new rollout entry into the cached list. */
function replaceRolloutDevice(list: AgentRolloutDevice[] | undefined, saved: AgentRolloutDevice): AgentRolloutDevice[] {
  const rest = (list ?? []).filter((d) => d.device_id !== saved.device_id);
  return [...rest, saved];
}

/** Make a computer a pilot or take it out (admins). */
export function useSetDevicePilot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, pilot }: { deviceId: string; pilot: boolean }) =>
      unwrap(api.PUT("/v1/devices/{device_id}/pilot", { params: { path: { device_id: deviceId } }, body: { pilot } })),
    onSuccess: (saved) => queryClient.setQueryData<AgentRolloutDevice[]>(keys.agentRolloutDevices, (list) => replaceRolloutDevice(list, saved)),
  });
}

/** Ask one computer to update at its next check (admins). */
export function useRequestDeviceUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deviceId: string) => unwrap(api.POST("/v1/devices/{device_id}/update-now", { params: { path: { device_id: deviceId } } })),
    onSuccess: (saved) => queryClient.setQueryData<AgentRolloutDevice[]>(keys.agentRolloutDevices, (list) => replaceRolloutDevice(list, saved)),
  });
}

/**
 * Ask for a link to set a new password (public). The answer is the same whether or not
 * an account uses the email.
 */
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (email: string) => unwrap(api.POST("/v1/auth/password-reset", { body: { email } })),
  });
}

/** Set a new password with the token from a reset link (public). Every session of the person ends. */
export function useConfirmPasswordReset() {
  return useMutation({
    mutationFn: (body: { token: string; password: string }) => unwrap(api.POST("/v1/auth/password-reset/confirm", { body })),
  });
}

/** How often the Connect page asks whether the computer picked its session up, and for how long. */
export const CONNECT_PICKUP_POLL_MS = 2_000;
export const CONNECT_PICKUP_WAIT_MS = 60_000;

/**
 * The computer behind a connect code (a web session of an organisation role). While the
 * person's approval waits for the computer to collect its session, the answer is polled
 * until `pollUntil` so the page can say "connected" only once it really is.
 */
export function useDeviceConnection(code: string, pollUntil?: number) {
  return useQuery({
    queryKey: keys.deviceConnection(code),
    queryFn: () => unwrap(api.GET("/v1/auth/device/connect", { params: { query: { code } } })),
    enabled: code !== "",
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.status === "approved" && pollUntil !== undefined && Date.now() < pollUntil ? CONNECT_PICKUP_POLL_MS : false,
  });
}

/** Approve or refuse signing in on the computer behind a code, as the signed-in person. */
export function useDecideDeviceConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { code: string; decision: "approve" | "deny" }) => unwrap(api.POST("/v1/auth/device/connect", { body })),
    onSuccess: (saved: DeviceConnection, { code }) => {
      queryClient.setQueryData<DeviceConnection>(keys.deviceConnection(code), saved);
      void queryClient.invalidateQueries({ queryKey: keys.devices }); // the Devices page shows the computer
    },
  });
}

/** An admin gives a person in their organisation a temporary password. Their sessions end. */
export function useSetUserPassword(userId: string) {
  return useMutation({
    mutationFn: (password: string) => unwrap(api.POST("/v1/users/{user_id}/password", { params: { path: { user_id: userId } }, body: { password } })),
  });
}

/**
 * A person's profile picture as a Blob. The request needs the access token, so it goes
 * through the API client instead of an <img src>, which would need the token in the address.
 * The key carries the version: a new picture is a new key, and a picture never changes
 * under the same one, so it is never refetched. `v` makes the address differ per picture,
 * so the browser's cache never answers with an old one either.
 */
export function usePicture(userId: string | undefined, version: string | undefined) {
  return useQuery({
    queryKey: keys.picture(userId ?? "", version ?? ""),
    queryFn: () =>
      unwrap(api.GET("/v1/users/{user_id}/picture", { params: { path: { user_id: userId! }, query: { v: version } }, parseAs: "blob" })) as Promise<Blob>,
    enabled: Boolean(userId && version),
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * The signed-in person's organisation's logo as a Blob, fetched with the token like a profile
 * picture. The key carries the version, so a logo never changes under the same key and is
 * never refetched; `v` keeps the browser's cache from answering with an old one.
 */
export function useOrganisationLogo(version: string | undefined) {
  return useQuery({
    queryKey: keys.logo(version ?? ""),
    queryFn: () => unwrap(api.GET("/v1/organisation/logo", { params: { query: { v: version } }, parseAs: "blob" })) as Promise<Blob>,
    enabled: Boolean(version),
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/* ------------------------------------------------------------ Screenshots */

/**
 * The API's largest page of screenshots. A day at the 5-minute floor with four monitors is
 * over a thousand images, so the console asks for 200 at a time and "Load more" brings the rest.
 */
export const SCREENSHOT_PAGE = 200;

/** How long a day's list is trusted before a look at it asks again: a new capture every few minutes. */
const SCREENSHOTS_STALE_MS = 30_000;

/**
 * One person's screenshots of one calendar day (in the organisation's time zone), oldest first.
 * Admins and managers name the person; the server records the look in the audit log. Nothing
 * is asked until both the person and the day are known.
 */
export function useScreenshots(userId: string | undefined, day: string | undefined) {
  return useInfiniteQuery({
    queryKey: keys.screenshotsDay(userId ?? "", day ?? ""),
    queryFn: ({ pageParam }) =>
      unwrap(api.GET("/v1/screenshots", { params: { query: { user_id: userId!, day: day!, cursor: pageParam, limit: SCREENSHOT_PAGE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
    enabled: Boolean(userId && day),
    staleTime: SCREENSHOTS_STALE_MS,
  });
}

/** The signed-in person's own screenshots of one day, with the ones removed at their request as placeholders. */
export function useMyScreenshots(day: string | undefined) {
  return useInfiniteQuery({
    queryKey: keys.screenshotsDay("me", day ?? ""),
    queryFn: ({ pageParam }) => unwrap(api.GET("/v1/screenshots", { params: { query: { day: day!, cursor: pageParam, limit: SCREENSHOT_PAGE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
    enabled: Boolean(day),
    staleTime: SCREENSHOTS_STALE_MS,
  });
}

/**
 * A screenshot's picture as a Blob, fetched with the token like a profile picture (never in an
 * <img src> address). The bytes never change under the same id, so it is never refetched; a 404
 * (removed since the list was read) is shown as the placeholder, not retried.
 */
const imageQuery = { staleTime: Infinity, gcTime: 10 * 60_000, refetchOnWindowFocus: false, retry: false } as const;

export function useScreenshotThumbnail(id: string, enabled = true) {
  return useQuery({
    queryKey: keys.screenshotThumb(id),
    queryFn: () => unwrap(api.GET("/v1/screenshots/{screenshot_id}/thumbnail", { params: { path: { screenshot_id: id } }, parseAs: "blob" })) as Promise<Blob>,
    enabled,
    ...imageQuery,
  });
}

export function useScreenshotImage(id: string, enabled = true) {
  return useQuery({
    queryKey: keys.screenshotImage(id),
    queryFn: () => unwrap(api.GET("/v1/screenshots/{screenshot_id}/image", { params: { path: { screenshot_id: id } }, parseAs: "blob" })) as Promise<Blob>,
    enabled,
    ...imageQuery,
  });
}

/** The removal queue (admins): the requests waiting for a decision, or the decided ones, newest first. */
export function useRemovalRequests(state: RemovalRequestState, enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.removalRequests(state),
    queryFn: ({ pageParam }) => unwrap(api.GET("/v1/screenshot-removal-requests", { params: { query: { state, cursor: pageParam, limit: PAGE_SIZE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
    enabled,
  });
}

/** Ask an admin to remove one of your own screenshots, saying why. The day's list shows it as waiting. */
export function useRequestScreenshotRemoval(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => unwrap(api.POST("/v1/screenshots/{screenshot_id}/removal-request", { params: { path: { screenshot_id: id } }, body: { reason } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.screenshots }),
  });
}

/** Approve (the image is deleted at once) or reject (with a note the person sees) a removal request. Admins. */
export function useDecideScreenshotRemoval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, note }: { id: string; decision: "approve" | "reject"; note?: string }): Promise<ScreenshotRemovalRequest> => {
      const params = { path: { request_id: id } };
      const body = note ? { note } : {};
      return decision === "approve"
        ? unwrap(api.POST("/v1/screenshot-removal-requests/{request_id}/approve", { params, body }))
        : unwrap(api.POST("/v1/screenshot-removal-requests/{request_id}/reject", { params, body }));
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.screenshots }),
  });
}

/** Remove a screenshot for everyone, at once (admins). Audited. */
export function useDeleteScreenshot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/screenshots/{screenshot_id}", { params: { path: { screenshot_id: id } } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.screenshots }),
  });
}

/** Flatten the pages of an infinite query into one list. */
export function allItems<T>(data: { pages: Page<T>[] } | undefined): T[] {
  return data?.pages.flatMap((page) => page.items) ?? [];
}
